import React, {  useEffect, useRef, useState } from "react";
 
import {  convertToZonedTimestamp} from "common/helpers/dateAndTimeHelpersV2";
import { CalendarContainer } from "./Calendar.elements"; 
import {  generateCalendarMonth, months, weekDays } from "./";
import { ArrowButton } from "./CalendarButtons";
 

 

/**
 * calendar takes incoming start and end times, along with a timezone and converts 
 * that timestamp to a local timezone and makes a new numeric date timestamp that translates t0 (so now 2025-01-01T00:00:00)
 * once the timestamp is localized it's converted to a number and sent to the calendar
 * 
 */
interface CalendarProps { 
  hasFutureDates?: boolean,
  monthsLimit?: number, 
  daysLimit?: number,
  timeZone?: string, 
  startTime?: string | number | Date, endTime?: string | number | Date, 
  handleChange?: ({startTime, endTime}: { startTime?: string | number | Date, 
  endTime?: string | number | Date }) => undefined ,
  maxStartDate?: string | number | Date
}

export const Calendar = ({ 
  daysLimit = 60,
  hasFutureDates = false,
  timeZone = 'UTC',
  startTime = new Date().getTime(),
  endTime = new Date().getTime(),
  monthsLimit,
  maxStartDate,
  handleChange 
}: CalendarProps): JSX.Element => {
 
  const containerRef = useRef<HTMLDivElement>(null);  

  const [year, month] = (convertToZonedTimestamp(startTime as number, timeZone, 'yyyy-MM') || '').split('-').map(Number)
  const [{curYear, curMonth }, setSelectedMonth] = useState({
    curYear: Number(year), 
    curMonth: Number(month - 1)
  })

  const nowTime = new Date().getTime()

  let endsToday = true
  let isSingleDay = false

  if ((convertToZonedTimestamp(endTime, timeZone) || '').includes('00:00:00')) endsToday = false
  if ((Number(endTime || 0) - Number(startTime || 0)) === 86400000) isSingleDay = true
  if (startTime === endTime) isSingleDay = true
  
  const startString = convertToZonedTimestamp(startTime as number, timeZone, `yyyy-MM-dd'T'00:00:00XXX`) as string
  const endString = convertToZonedTimestamp(endTime as number, timeZone, `yyyy-MM-dd'T'00:00:00XXX`) as string
 
  const zonedCalendar = generateCalendarMonth(curYear, curMonth, timeZone)
  
  const className = 'calendar'

  const handleNextMonth = () => {
    let nextMonth = curMonth + 1
    let nextYear = curYear
    if (curMonth > 11) {
      nextMonth = 0;
      nextYear += 1;
    }

    setSelectedMonth({curYear: nextYear, curMonth: nextMonth })
  }
  
  const handlePrevMonth = ( ) => {
    let prevMonth = curMonth - 1
    let prevYear = curYear
    if (curMonth === 0) { 
      prevMonth = 11;
      prevYear -= 1;
    } 
    setSelectedMonth({curYear: prevYear, curMonth: prevMonth })
  }

  const handleDayClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const elTimestamp = Number(e.currentTarget.dataset.timestamp)
    const now = new Date().getTime()

    if (hasFutureDates === false && elTimestamp > now) return 
    const elZone = e.currentTarget.dataset.zoned || ''
    const containerEl = e.currentTarget.closest?.('[data-id="custom-calendar"]') as HTMLDivElement
    
    const contFirst = containerEl.dataset?.first
    const contLast = containerEl.dataset?.last
    
    if (!contFirst || contLast) {
      containerEl.dataset.first = elZone
      delete containerEl.dataset.last
 
      handleChange?.({startTime: elTimestamp, endTime: elTimestamp })
    } else {
      containerEl.dataset.last = elZone 
      const start = new Date(contFirst).getTime()
      const end = elTimestamp // add one day to the end time
      handleChange?.({startTime: Math.min(start, end), endTime: Math.max(start, end)})
      delete containerEl.dataset?.first
      delete containerEl.dataset?.last
      containerEl.classList.add('has-range')
    }
  }
  
  
  const handleDayEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    const containerEl = e.currentTarget.closest?.('[data-id="custom-calendar"]') as HTMLDivElement
    
    const firstDate = containerEl.dataset?.first
    const lastDate = containerEl.dataset?.last
    const first = firstDate && new Date(firstDate || '').getTime()
    const last = lastDate && new Date(lastDate || '').getTime()
    
    if (!first) return
    if (last && first !== last) return
    const endTimestamp = Number(e.currentTarget.dataset.timestamp || 0)
    
    e?.currentTarget?.closest?.('[data-id="calendar"]')?.querySelectorAll('[data-id="day-button"]')?.forEach((el) => {
      el.classList.remove('is-in-range','is-hovered')
      
      const elTimestamp = Number(el.dataset.timestamp || 0)
      let inRange = false
      if (elTimestamp >= first && elTimestamp <= endTimestamp) inRange = true
      if (elTimestamp <= first && elTimestamp >= endTimestamp) inRange = true
      
      if (inRange) el.classList.add('is-in-range')
      else el.classList.remove('is-in-range')
    })
  }

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.dataset.start = startString
      containerRef.current.dataset.end = endString
      if (startString !== endString) containerRef.current.classList.add('has-range')
    }
  
  }, [startString, endString, containerRef])
  
  let newEnd = endString

  if (monthsLimit === 12) daysLimit = 365

  if (!isSingleDay) newEnd =  convertToZonedTimestamp(new Date(endString).getTime() - 86400000, timeZone, `yyyy-MM-dd'T'00:00:00XXX`)
  

  return (
    <CalendarContainer ref={containerRef} className={className} data-id='custom-calendar'  data-start={startString || undefined} data-end={newEnd || undefined}>

      <div className='month-year-header'>
        <ArrowButton left onClick={handlePrevMonth} />
        <div className='title'><span className='month'>{months[curMonth]}</span>&nbsp;<span className='year'>{curYear}</span></div>
        <ArrowButton right onClick={handleNextMonth} />
      </div>

      <div className='calendar-grid' data-id='calendar'>
        
        {weekDays.map((day) => (
          <div key={day} className='title-cell cell'>
            {day}
          </div>
        ))}
        
        {zonedCalendar.map((week, weekIdx) => week.map((timestamp, dayIdx) => {
          const dateStamp = timestamp.split('T')[0].split('-')
          const [tsYear, tsMonth, tsDay] = dateStamp
          const weekDay = convertToZonedTimestamp(timestamp, timeZone, 'EEE') as string
          const weekDayPos = weekDays.indexOf(weekDay)
          const time = new Date(timestamp).getTime()
          let isDisabled = false
          //const tsMonth = timestamp && new Date(timestamp).getMonth() || 0 
          let className = `current-month day-cell cell`
          if (timestamp === startString) className += ' is-start'
          if (timestamp === endString) className += ' is-end'
          // is in the futre
          if (
            time > nowTime && hasFutureDates === false || 
            time < (nowTime - (daysLimit * 86400000)) || 
            maxStartDate && new Date(maxStartDate).getTime() < time
          ) isDisabled = true
        
          
          if (isDisabled) className += ' is-disabled'
         
          if (startString && endString) {
            const s = new Date(startString).getTime()
            const e = new Date(newEnd).getTime()
            if (time >= s && time <= e) className += ' is-in-range'
          }
           
          return (
            <button
              style={{gridColumn: weekDayPos + 1}}
              className={className}
              data-loc={dayIdx}
              data-weekday={weekDay}
              data-zoned={timestamp}
              data-month={tsMonth}
              data-year={tsYear}
              data-day={tsDay}
              data-utc={new Date(timestamp).toISOString()}
              data-timestamp={time}
              data-id='day-button'
              key={`week${weekIdx}day${dayIdx}`}
              onClick={!isDisabled ? handleDayClick : undefined}
              onMouseEnter={handleDayEnter}
            >
              <div className='indicator'></div>
              <div className='label'>{Number(tsDay)}</div>
              
            </button>
          )
        }))}
        
      </div>
    </CalendarContainer>
  )
} 